<script setup lang="ts">
import { Ref,ref, inject, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, } from '@/components/ui/dialog'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'vue-sonner'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Loader2 } from 'lucide-vue-next'

const vhostId = inject<Ref<string> | null>('vhostId', null);


// Types
interface WidenField {
	id: string
	label: string
	type: 'textfield' | 'textarea' | 'datefield' | 'dropdown' | 'checkbox'
	source: 'api' | 'derived' | 'manual'
	settings?: {
		options?: string[]
		truevalue?: string
		falsevalue?: string
		defaultCategories?: any[]
		calculatedField?: string
		delim?: string
	}
}

interface WidenMetadata {
	display_name: string
	// Add other properties as needed
}

interface ImportStatus {
	status: 0 | 1 | 2 // 0 = pending, 1 = success, 2 = error
	info: string
}

interface ClientTransfer {
	id: string
	name: string
	options: {
		widenDatatoImportMin: Record<string, number>
	}
}

// State
const currentStep = ref<number>(0)
const importType = ref<'ref' | 'cid'>('ref')
const widenIDs = ref<string>('')
const collectionID = ref<string>('')
const allFieldsSelected = ref<boolean>(false)
const hideDerivedFields = ref<boolean>(true)
const showSettingsModal = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const totalImported = ref<number>(0)
const showPublishOptions = ref<boolean>(false)

const userSettings = reactive({
	id:0,
	meta: {
		delim: ';',
		widenDataToImport: {} as Record<string, boolean>
	}
})

const defaultFields = reactive<Record<string, boolean>>({})
const minimalFields = reactive<Record<string, boolean>>({})
const selectedMinimalFields = reactive<Record<string, boolean>>({})

const widenFields = ref<WidenMetadata[]>([])
const projectWidenData = ref<WidenField[]>([])
const clientTransfers = ref<ClientTransfer[]>([])
const importStatus = reactive<Record<string, ImportStatus>>({})
const importedMediaList = ref<string[]>([])
const newProperties = reactive<Record<string, string>>({})
const appendFields = ref<string[]>([])

// Computed
const selectedFieldCount = computed(() => {
	return Object.values(defaultFields).filter(val => val ).length
})

const selectedMinimalFieldCount = computed(() => {
	return Object.values(selectedMinimalFields).filter(val => val ).length
})

const sortedProjectWidenData = computed(() => {
	return [...projectWidenData.value].sort((a, b) => a.label.localeCompare(b.label))
})

const canImport = computed(() => {
	if (importType.value === 'ref') {
		return selectedFieldCount.value > 0 && widenIDs.value.trim() !== ''
	} else {
		return selectedMinimalFieldCount.value > 0 && collectionID.value.trim() !== ''
	}
})

// Methods
const fetchLoggedInUser = async () => {
	try {
		const response = await axios.post('/services/json', {
			method: 'users.getLoggedInUser'
		})

		if (response.data.status) {
			Object.assign(userSettings, response.data.result)

			if (!userSettings.meta.widenDataToImport) {
				userSettings.meta.widenDataToImport = {}
			} else {
				// Initialize defaultFields with user selections (all unchecked initially)
				const selectedFields = Object.entries(userSettings.meta.widenDataToImport)
					.filter(([_, value]) => value )
					.reduce((acc, [key]) => {
						acc[key] = false // Start with all unchecked
						return acc
					}, {} as Record<string, boolean>)

				Object.assign(defaultFields, selectedFields)
			}
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to fetch user data',
			variant: 'destructive'
		})
	}
}

const fetchWidenFields = async () => {
	try {
		if(vhostId) {
			const response = await axios.post('/services/json', {
				method: 'vhost.getWidenMetadata',
				vhostID: vhostId.value,
				transferID: 'auto'
			})

			if (response.data.status) {
				widenFields.value = response.data.result.fields
			}
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to fetch Widen fields',
			variant: 'destructive'
		})
	}
}

const fetchCustomMeta = async () => {
	try {
		if(vhostId) {
			const response = await axios.post('/services/json', {
				method: 'vhost.getCustomMeta',
				vhostID: vhostId.value,
				provider: 'widen'
			})

			if (response.data.status) {
				projectWidenData.value = response.data.result
			}
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to fetch custom metadata',
			variant: 'destructive'
		})
	}
}

const fetchClientTransfers = async () => {
	try {
		if(vhostId) {
			const response = await axios.post('/services/json', {
				method: 'vhost.getClientTransferList',
				vhostID: vhostId.value,
				protocol: 'widen'
			})

			if (response.data.status) {
				clientTransfers.value = response.data.result
				if (clientTransfers.value.length > 0) {
					Object.assign(minimalFields, clientTransfers.value[0].options.widenDatatoImportMin)
				}
			}
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to fetch client transfers',
			variant: 'destructive'
		})
	}
}

const saveUserSettings = async () => {
	try {
		if(vhostId) {
			// Only save fields that are selected (value = 1)
			const settingsToSave = Object.fromEntries(
				Object.entries(userSettings.meta.widenDataToImport)
				.filter(([_, value]) => value )
			)

			const response = await axios.post('/services/json', {
				method: 'users.updateUserInfo',
				vhost: vhostId.value,
				id: userSettings.id,
				newProperties: { meta: { ...userSettings.meta, widenDataToImport: settingsToSave } }
			})

			if (response.data.status) {
				toast({
					title: 'Success',
					description: 'Settings saved successfully'
				})
				showSettingsModal.value = false
			} else {
				throw new Error(response.data.result)
			}
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to save settings',
			variant: 'destructive'
		})
	}
}

const selectAllFields = () => {
	Object.keys(userSettings.meta.widenDataToImport).forEach(key => {
		userSettings.meta.widenDataToImport[key] = true
	})
}

const selectNoFields = () => {
	Object.keys(userSettings.meta.widenDataToImport).forEach(key => {
		userSettings.meta.widenDataToImport[key] = false
	})
}

const toggleAllDefaultFields = (select: boolean) => {
	Object.keys(defaultFields).forEach(key => {
		defaultFields[key] = select ? true : false 
	})
	allFieldsSelected.value = select
}

const toggleAllMinimalFields = (select: boolean) => {
	Object.keys(minimalFields).forEach(key => {
		selectedMinimalFields[key] = select ? true : false 
	})
}

const importAssets = async () => {
	isLoading.value = true
	currentStep.value = 1
	totalImported.value = 0
	importedMediaList.value = []
	Object.keys(newProperties).forEach(key => delete newProperties[key])
	appendFields.value = []
	showPublishOptions.value = false

	if (importType.value === 'ref') {
		await importByReferenceId()
	} else {
		await importByCollectionId()
	}

	isLoading.value = false
	currentStep.value = 2
}

const importByReferenceId = async () => {
	const ids = widenIDs.value.trim().split(/\r?\n/).filter(id => id.trim() !== '')

	// Initialize status tracking
	ids.forEach(id => {
		importStatus[id] = { status: 0, info: '' }
	})

	// Filter selected fields
	const selectedFields = Object.fromEntries(
		Object.entries(defaultFields).filter(([_, value]) => value )
	)

	try {
		if(vhostId) {
			const promises = ids.map(async id => {
				try {
					const response = await axios.post('/services/json', {
						method: 'media.getWidenData',
						vhost: vhostId.value,
						query: `ReferenceID:(${id})`,
						createMedia: true,
						widenId: id,
						importFields: selectedFields
					})

					if (response.data.status && response.data.result[0]?.mediaId) {
						importStatus[id] = { status: 1, info: response.data.result[0].mediaId }
						importedMediaList.value.push(response.data.result[0].mediaId)
						totalImported.value++
					} else {
						importStatus[id] = { 
							status: 2, 
							info: response.data.result?.[0]?.message || 'Unknown error' 
						}
					}
				} catch (error: any) {
					importStatus[id] = { status: 2, info: error.message }
				}
			})

			await Promise.all(promises)
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'An error occurred during import',
			variant: 'destructive'
		})
	}
}

const importByCollectionId = async () => {
	const collectionIds = collectionID.value.trim().split(/\r?\n/).filter(id => id.trim() !== '')

	// Filter selected fields
	const selectedFields = Object.fromEntries(
		Object.entries(selectedMinimalFields).filter(([_, value]) => value)
	)

	try {
		if(vhostId) {
			const promises = collectionIds.map(async colId => {
				try {
					const response = await axios.post('/services/json', {
						method: 'media.getWidenData',
						vhost: vhostId.value,
						query: `AssetType:Video+CollectionID:(${colId})`,
						createMedia: true,
						importAll: true,
						importFields: selectedFields
					})

					if (response.data.status) {
						response.data.result.forEach((item: any) => {
							importStatus[item.widenId] = { status: 1, info: item.mediaId }
							importedMediaList.value.push(item.mediaId)
						})
						totalImported.value += response.data.result.length
					}
				} catch (error: any) {
					toast({
						title: `Error importing collection ${colId}`,
						description: error.message,
						variant: 'destructive'
					})
				}
			})

			await Promise.all(promises)
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'An error occurred during import',
			variant: 'destructive'
		})
	}
}

const updateMediaMetadata = async () => {
	try {
		const response = await axios.post('/services/json', {
			method: 'media.bulkMetaUpdate',
			id: importedMediaList.value,
			delimiter: userSettings.meta.delim,
			append: appendFields.value,
			newMeta: newProperties.value
		})

		if (response.data.status) {
			const successCount = response.data.result.successful.length
			const failCount = response.data.result.failed.length

			toast({
				title: 'Metadata Updated',
				description: `${successCount} items updated successfully, ${failCount} failed`
			})

			showPublishOptions.value = true
		} else {
			throw new Error(response.data.result)
		}
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to update metadata',
			variant: 'destructive'
		})
	}
}

const publishToWiden = async (transferId: string) => {
	try {
		const promises = importedMediaList.value.map(async mediaId => {
			await axios.post('/services/json', {
				method: 'media.startClientTransfers',
				mid: mediaId,
				tid: transferId
			})
		})

		await Promise.all(promises)

		toast({
			title: 'Success',
			description: `${importedMediaList.value.length} items queued for transfer to Widen`
		})

		showPublishOptions.value = false
	} catch (error) {
		toast({
			title: 'Error',
			description: 'Failed to publish to Widen',
			variant: 'destructive'
		})
	}
}

const resetForm = () => {
	currentStep.value = 0
	widenIDs.value = ''
	collectionID.value = ''
	importedMediaList.value = []
	Object.keys(importStatus).forEach(key => delete importStatus[key])
	totalImported.value = 0
	Object.keys(newProperties).forEach(key => delete newProperties[key])
	appendFields.value = []
	showPublishOptions.value = false
}

// Lifecycle
onMounted(() => {
	fetchLoggedInUser()
	fetchWidenFields()
	fetchCustomMeta()
	fetchClientTransfers()
})
</script>

<template>
	<div class="container mx-auto p-4 ">
		<!-- Stepper -->

		<!-- Step 1: Select Import Type -->
		<div v-if="currentStep === 0" class="space-y-6">
			<div class="flex justify-between items-top">
				<div class="space-y-2">
					<h2 class="text-2xl font-bold">Import from Widen</h2>
					<p class="text-muted-foreground">Select the type of import you want to perform</p>
				</div>
				<div  class="text-center ">
					<Button @click="showSettingsModal = true"> Select Default Fields </Button>
				</div>
			</div>

			<Separator />

			<RadioGroup v-model="importType" class=" flex justify-start items-center">
			<div class="flex items-center ">
				<RadioGroupItem value="ref" id="import-ref" />
				<Label for="import-ref" class="px-2">By Reference ID</Label>
			</div>
			<div class="flex items-center ">
				<RadioGroupItem value="cid" id="import-cid" />
				<Label for="import-cid" class="px-2">By Collection ID</Label>
			</div>
			</RadioGroup>

			<div v-if="importType === 'ref'" class="space-y-4 ">
				<div  class="space-y-4">
					<div>
						<Label for="widen-ids">Enter Widen Asset IDs (one per line)</Label>
						<Textarea id="widen-ids" v-model="widenIDs" class="min-h-[150px] mt-3" placeholder="Paste Widen Asset IDs here, one per line" />
					</div>

					<div class="space-y-2">
						<div class="flex justify-between items-center">
							<div>
								<h3 class="font-medium">Fields to Import</h3>
							</div>
							<div>
									<Button variant="link" size="sm" @click="toggleAllDefaultFields(!allFieldsSelected)" >
										{{ allFieldsSelected ||  Object.keys(defaultFields).length>30 ? 'Deselect All' : 'Select All' }}
									</Button> | 
								<Button variant="link" size="sm" @click="Object.assign(defaultFields,userSettings.meta.widenDataToImport)" >
									Select my Defaults
								</Button>   
							</div>
						</div>

						<div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
							<div v-for="(value, field) in defaultFields" :key="field" class="flex items-center space-x-2">
								<Switch v-model:checked="defaultFields[field]" :id="`field-${field}`" />
								<Label :for="`field-${field}`">{{ field }}</Label>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-else class="space-y-4">
				<div>
					<Label for="collection-ids">Enter Widen Collection IDs (one per line)</Label>
					<Textarea id="collection-ids" v-model="collectionID" class="min-h-[250px]" placeholder="Paste Widen Collection IDs here, one per line" />
				</div>

				<div class="space-y-2">
					<div class="flex justify-between items-center">
						<div>
							<h3 class="font-medium">Fields to Import</h3>
						</div>
						<div>
							<Button variant="link" size="sm" @click="toggleAllMinimalFields(Object.keys(selectedMinimalFields).length < Object.keys(minimalFields).length)" >
								{{ Object.keys(selectedMinimalFields).length > Object.keys(minimalFields).length ? 'Deselect All' : 'Select All' }}
								</Button> | 
							<Button variant="link" size="sm" @click="Object.assign(selectedMinimalFields,userSettings.meta.widenDataToImport)" >
								Select my Defaults
							</Button> 
						</div>
					</div>

					<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						<div v-for="(value, field) in minimalFields" :key="field" class="flex items-center space-x-2">
							<Switch v-model:checked="selectedMinimalFields[field]" :id="`min-field-${field}`" />
							<Label :for="`min-field-${field}`">{{ field }}</Label>
						</div>
					</div>
				</div>
			</div>

			<div class="flex justify-end">
				<Button @click="importAssets" :disabled="!canImport">
					Start Import
				</Button>
			</div>
		</div>

		<!-- Step 2: Import Progress -->
		<div v-else-if="currentStep === 1" class="space-y-6">
			<div class="space-y-2">
				<h2 class="text-2xl font-bold">Importing Assets</h2>
				<p class="text-muted-foreground">Please wait while we import your assets from Widen</p>
			</div>

			<Separator />

			<div v-if="isLoading" class="flex flex-col items-center justify-center py-12">
				<Loader2 class="h-12 w-12 animate-spin text-primary mb-4" />
				<p class="text-lg">Importing media, please wait...</p>
			</div>

			<div class="space-y-2">
				<div class="flex justify-between">
					<h3 class="font-medium">Import Status</h3>
					<Badge variant="outline">
					{{ totalImported }} / {{ Object.keys(importStatus).length }} imported
					</Badge>
				</div>

				<div class="border rounded-lg divide-y">
					<div v-for="(status, id) in importStatus" :key="id" class="p-4 flex items-center justify-between">
						<span>{{ id }}</span>
						<div class="flex items-center space-x-2">
							<span v-if="status.status === 0" class="text-muted-foreground">Pending</span>
							<span v-if="status.status === 1" class="text-green-500">Imported</span>
							<span v-if="status.status === 2" class="text-red-500">Error: {{ status.info }}</span>
							<Button v-if="status.status === 1" variant="outline" size="sm" as-child >
								<a :href="`/mediaitem/${status.info}?vid=${vhostId}`" target="_blank">View</a>
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Step 3: Update Metadata -->
		<div v-else class="space-y-6">
			<div class="space-y-2">
				<h2 class="text-2xl font-bold">Update Metadata</h2>
				<p class="text-muted-foreground">Edit metadata for imported assets</p>
			</div>

			<Separator />

			<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div v-for="field in sortedProjectWidenData" :key="field.id"
				     v-show="(importType === 'ref' ? defaultFields[field.label]  : selectedMinimalFields[field.label] ) && (field.source !== 'derived' || !hideDerivedFields)"
				     class="space-y-2"
				     >
				     <div class="flex justify-between items-center">
					     <Label :for="`meta-${field.id}`">{{ field.label }}</Label>
					     <div class="flex space-x-1">
						     <Button v-if="field.source === 'api'" variant="ghost" size="icon" class="h-8 w-8" >
							     <SearchIcon class="h-4 w-4" />
						     </Button>
							     <Button variant="ghost" size="sm" :class="appendFields.includes(`widen:${field.label}`) ? 'bg-muted' : ''" @click="appendFields.includes(`widen:${field.label}`) ?  appendFields.splice(appendFields.indexOf(`widen:${field.label}`), 1) : appendFields.push(`widen:${field.label}`)" >
								     {{ appendFields.includes(`widen:${field.label}`) ? 'Appending' : 'Replace' }}
							     </Button>
					     </div>
				     </div>

				     <Input v-if="field.type === 'textfield'" :id="`meta-${field.id}`" v-model="newProperties[`widen:${field.label}`]" />

				     <Textarea v-if="field.type === 'textarea'" :id="`meta-${field.id}`" v-model="newProperties[`widen:${field.label}`]" class="min-h-[150px]" />
					     <Input v-if="field.type === 'datefield'" :id="`meta-${field.id}`" type="date" v-model="newProperties[`widen:${field.label}`]" />

					     <select v-if="field.type === 'dropdown'" :id="`meta-${field.id}`" v-model="newProperties[`widen:${field.label}`]" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" >
						     <option value="">Choose Option</option>
						     <option v-for="option in field.settings?.options" :key="option" :value="option">
						     {{ option }}
						     </option>
					     </select>

					     <div v-if="field.type === 'checkbox'" class="flex items-center space-x-2">
						     <input type="checkbox" :id="`meta-${field.id}`" v-model="newProperties[`widen:${field.label}`]" :true-value="field.settings?.truevalue" :false-value="field.settings?.falsevalue" />
						     <Label :for="`meta-${field.id}`">{{ field.label }}</Label>
					     </div>
				</div>
			</div>

			<div class="flex justify-between">
				<Button variant="outline" @click="resetForm">
					Import More Assets
				</Button>
				<div class="space-x-2">
					<Button v-if="showPublishOptions && clientTransfers.length > 0" @click="publishToWiden(clientTransfers[0].id)" >
						Publish to Widen
					</Button>
						<Button @click="updateMediaMetadata">
							Apply Changes
						</Button>
				</div>
			</div>
		</div>

		<!-- Settings Modal -->
		<Dialog v-model:open="showSettingsModal">
			<DialogContent class="sm:max-w-4xl">
			<DialogHeader>
			<DialogTitle>Select Default Fields to Import</DialogTitle>
			</DialogHeader>

			<div class="space-y-4">
				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<Label for="delimiter">Delimiter</Label>
						<Input id="delimiter" v-model="userSettings.meta.delim" placeholder=";"  />
					</div>
					<div class="flex items-end justify-end space-x-2">
						<Button variant="link" size="sm" @click="selectAllFields">
							Select All
						</Button>
						<Button variant="link" size="sm" @click="selectNoFields">
							Select None
						</Button>
					</div>
				</div>

				<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto p-2">
					<div v-for="field in widenFields" :key="field.display_name" class="flex items-center space-x-2">
						<Switch v-model:checked="userSettings.meta.widenDataToImport[field.display_name]" :id="`setting-${field.display_name}`" />
						<Label :for="`setting-${field.display_name}`">{{ field.display_name }}</Label>
					</div>
				</div>
			</div>

			<DialogFooter>
			<Button variant="outline" @click="showSettingsModal = false">
				Cancel
			</Button>
			<Button @click="saveUserSettings">
				Save Settings
			</Button>
			</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>
